// The one fiscal-year convention of the frontend: the period selector, Targets and the Manual entry grid all use it.
// The energy plan's year for the selected time period, and its label.
//
// A plan year is the year its fiscal year ENDS (Sentinel's convention, NomOS plan_node.plan_year and the period
// selector's `fy_2027` / "FY27"): with a September start, FY27 runs 1 Sep 2026 - 31 Aug 2027. So "FY to date" on
// 20 Oct 2026 (1 Sep 2026 - 19 Oct 2026) is plan year 2027, not 2026 (the calendar year of its first day).

/** Fiscal year (the year it ends) that local calendar day `d` falls in. */
export function fiscalYearOf(d: Date, fiscalStartMonth: number): number {
  if (fiscalStartMonth <= 1) return d.getFullYear()
  return d.getMonth() + 1 >= fiscalStartMonth ? d.getFullYear() + 1 : d.getFullYear()
}

/**
 * The plan year a period asks about. An explicit fiscal year (`fy_2027`, `FY27`) is that year; any date range
 * (FY to date, calendar YTD, last 12 months, a custom range) is the fiscal year its last day falls in - the year
 * in progress at the end of the range.
 */
export function planYearOfPeriod(period: { periodKey: string; start: Date; end: Date }, fiscalStartMonth: number): number {
  const key = period.periodKey
  if (key.startsWith('fy_')) {
    const y = parseInt(key.split('_')[1], 10)
    if (Number.isFinite(y)) return y
  }
  if (/^FY\d{2}$/.test(key)) return 2000 + parseInt(key.slice(2), 10)
  return fiscalYearOf(period.end, fiscalStartMonth)
}

/** "FY27" - the label the period selector uses for the same year. */
export function fiscalYearLabel(planYear: number): string {
  return `FY${String(planYear % 100).padStart(2, '0')}`
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Sep 2026" or "Sep 2026 – Aug 2027" for a [start, end) window of whole months given as YYYY-MM-DD. */
export function monthWindowLabel(start: string, end: string): string {
  const [sy, sm] = start.split('-').map(Number)
  const [ey, em] = end.split('-').map(Number)
  // end is exclusive: the last whole month is the one before it
  const ly = em === 1 ? ey - 1 : ey
  const lm = em === 1 ? 12 : em - 1
  const first = `${MONTH_ABBR[sm - 1]} ${sy}`
  const last = `${MONTH_ABBR[lm - 1]} ${ly}`
  return first === last ? first : `${first} – ${last}`
}

/** The fiscal year (the year it ends) in progress today. */
export function currentFiscalYear(fiscalStartMonth: number, now: Date = new Date()): number {
  return fiscalYearOf(now, fiscalStartMonth)
}

/**
 * The 12 calendar months `YYYY-MM` of fiscal year `fy` (the year it ENDS), in fiscal order.
 * September start: FY2027 = 2026-09 … 2027-08. January start: FY2026 = 2026-01 … 2026-12.
 */
export function fiscalYearMonths(fy: number, fiscalStartMonth: number): string[] {
  const start = fiscalStartMonth >= 1 && fiscalStartMonth <= 12 ? fiscalStartMonth : 1
  const startYear = start === 1 ? fy : fy - 1
  const out: string[] = []
  for (let i = 0; i < 12; i++) {
    const idx = start - 1 + i
    out.push(`${startYear + Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`)
  }
  return out
}

/** "Sep 2026" for `2026-09`. */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number)
  return `${MONTH_ABBR[m - 1]} ${y}`
}

/** "FY2027 (Sep 2026 – Aug 2027)"; a calendar-year tenant's is just "FY2026". */
export function fiscalYearRangeLabel(fy: number, fiscalStartMonth: number): string {
  if (fiscalStartMonth <= 1) return `FY${fy}`
  const months = fiscalYearMonths(fy, fiscalStartMonth)
  return `FY${fy} (${monthLabel(months[0])} – ${monthLabel(months[11])})`
}

/**
 * The fiscal years a reading-entry selector offers: the current one and the `past` before it, newest first. No
 * future year: every month of it is still ahead, so a reading typed there is a reading for a month that has not happened.
 */
export function selectableFiscalYears(current: number, past = 4): number[] {
  return Array.from({ length: past + 1 }, (_, i) => current - i)
}

/** `value` when it is one of `options`, else the newest option (a stale or out-of-range URL/stored value never sticks). */
export function clampFiscalYear(value: number | null | undefined, options: number[]): number {
  if (typeof value === 'number' && Number.isInteger(value) && options.includes(value)) return value
  return Math.max(...options)
}

/** The clause that keeps the plan nodes of one plan year (the year its fiscal year ENDS): the Targets list's own scope. */
export function planYearClause(planYear: number): { attribute: string; op: 'eq'; value: number } {
  return { attribute: 'plan_year', op: 'eq', value: planYear }
}
